// Rate Computation & Underwriting Data for Cancer, Heart Attack, Stroke (CHAS) Lump Sum Plan

export const AGE_BANDS = [
  { minAge: 18, maxAge: 39, label: '20–37', cancerBase: 3.75, heartStrokeBase: 3.50, combinedBase: 7.25 },
  { minAge: 40, maxAge: 44, label: '40–44', cancerBase: 4.75, heartStrokeBase: 4.75, combinedBase: 9.50 },
  { minAge: 45, maxAge: 49, label: '45–49', cancerBase: 6.25, heartStrokeBase: 6.25, combinedBase: 12.50 },
  { minAge: 50, maxAge: 54, label: '50–54', cancerBase: 8.25, heartStrokeBase: 8.00, combinedBase: 16.25 },
  { minAge: 55, maxAge: 59, label: '55–59', cancerBase: 10.50, heartStrokeBase: 10.25, combinedBase: 20.75 },
  { minAge: 60, maxAge: 84, label: '60–64', cancerBase: 13.25, heartStrokeBase: 13.25, combinedBase: 26.50 }
];

export const COVERAGE_TIERS = [
  { value: 10000, label: '$10,000', cancer: 5000, heartStroke: 5000, fullUnderwriting: false },
  { value: 20000, label: '$20,000', cancer: 10000, heartStroke: 10000, fullUnderwriting: false },
  { value: 30000, label: '$30,000', cancer: 15000, heartStroke: 15000, fullUnderwriting: false },
  { value: 40000, label: '$40,000', cancer: 20000, heartStroke: 20000, fullUnderwriting: false },
  { value: 50000, label: '$50,000', cancer: 25000, heartStroke: 25000, fullUnderwriting: false },
  { value: 60000, label: '$60,000', cancer: 30000, heartStroke: 30000, fullUnderwriting: false },
  { value: 70000, label: '$70,000', cancer: 35000, heartStroke: 35000, fullUnderwriting: false },
  { value: 80000, label: '$80,000', cancer: 40000, heartStroke: 40000, fullUnderwriting: false },
  { value: 90000, label: '$90,000', cancer: 45000, heartStroke: 45000, fullUnderwriting: false },
  { value: 100000, label: '$100,000', cancer: 50000, heartStroke: 50000, fullUnderwriting: false },
  { value: 110000, label: '$110,000', cancer: 55000, heartStroke: 55000, fullUnderwriting: true },
  { value: 120000, label: '$120,000', cancer: 60000, heartStroke: 60000, fullUnderwriting: true },
  { value: 130000, label: '$130,000', cancer: 65000, heartStroke: 65000, fullUnderwriting: true },
  { value: 140000, label: '$140,000', cancer: 70000, heartStroke: 70000, fullUnderwriting: true },
  { value: 150000, label: '$150,000', cancer: 75000, heartStroke: 75000, fullUnderwriting: true }
];

export const getAgeBand = (age) => {
  const safeAge = Math.min(Math.max(parseInt(age, 10) || 35, 18), 84);
  const band = AGE_BANDS.find(b => safeAge >= b.minAge && safeAge <= b.maxAge);
  return band || AGE_BANDS[0];
};

export const calculateLumpSumRate = ({ age = 35, coverageAmount = 50000 }) => {
  const safeAge = Math.min(Math.max(parseInt(age, 10) || 35, 18), 84);
  const safeCoverage = Math.min(Math.max(parseInt(coverageAmount, 10) || 50000, 10000), 150000);
  
  const band = getAgeBand(safeAge);
  const rateMultiplier = safeCoverage / 10000;
  
  const cancerCoverage = safeCoverage / 2;
  const heartStrokeCoverage = safeCoverage / 2;
  
  const cancerPremium = Number((rateMultiplier * band.cancerBase).toFixed(2));
  const heartStrokePremium = Number((rateMultiplier * band.heartStrokeBase).toFixed(2));
  const totalMonthlyPremium = Number((cancerPremium + heartStrokePremium).toFixed(2));
  const totalAnnualPremium = Number((totalMonthlyPremium * 12).toFixed(2));
  
  const isFullUnderwriting = safeCoverage > 100000;

  return {
    age: safeAge,
    ageBand: band.label,
    coverageAmount: safeCoverage,
    cancerCoverage,
    heartStrokeCoverage,
    rateMultiplier,
    cancerBase: band.cancerBase,
    heartStrokeBase: band.heartStrokeBase,
    combinedBase: band.combinedBase,
    cancerPremium,
    heartStrokePremium,
    totalMonthlyPremium,
    totalAnnualPremium,
    isFullUnderwriting
  };
};

export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' }
];

export const calculateAgeFromDOB = (dobString) => {
  if (!dobString) return null;
  let birthDate = null;

  if (dobString.includes('-')) {
    const parts = dobString.split('-');
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        // YYYY-MM-DD
        birthDate = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      } else if (parts[2].length === 4) {
        // MM-DD-YYYY
        birthDate = new Date(parseInt(parts[2], 10), parseInt(parts[0], 10) - 1, parseInt(parts[1], 10));
      }
    }
  } else if (dobString.includes('/')) {
    const parts = dobString.split('/');
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        // YYYY/MM/DD
        birthDate = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      } else if (parts[2].length === 4) {
        // MM/DD/YYYY
        birthDate = new Date(parseInt(parts[2], 10), parseInt(parts[0], 10) - 1, parseInt(parts[1], 10));
      }
    }
  }

  if (!birthDate || isNaN(birthDate.getTime())) {
    try {
      const parsed = Date.parse(dobString);
      if (!isNaN(parsed)) birthDate = new Date(parsed);
    } catch (e) {}
  }
  
  if (!birthDate || isNaN(birthDate.getTime())) return null;
  
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return (age >= 0 && age <= 120) ? age : null;
};

export const getDOBLimits = (minAge = 18, maxAge = 84) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');

  const maxYear = currentYear - minAge;
  const minYear = currentYear - maxAge;

  const maxDate = `${maxYear}-${mm}-${dd}`;
  const minDate = `${minYear}-${mm}-${dd}`;

  const years = Array.from({ length: maxAge - minAge + 1 }, (_, i) => maxYear - i);

  return { minDate, maxDate, minYear, maxYear, years };
};

export const generateReferenceId = (prefix = 'IE-LS') => {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  const rand = Math.floor(100 + Math.random() * 900); // 3-digit discriminator

  return `${prefix}-${yy}${mm}${dd}-${hh}${min}${ss}-${rand}`;
};
